/**
 * Endpoints du domaine savings (savings-service, 10ᵉ microservice — guide
 * v3.1 §6.10) — source canonique. Chemins copiés TELS QUELS de la collection
 * Postman v3.1 (préfixe gateway `/gw/savings`). Ne jamais inventer un chemin.
 * `@/core/api/endpoints` ré-exporte ces valeurs : ajouter les routes ICI.
 *
 * Idempotency-Key OBLIGATOIRE (§4.7) sur tous les endpoints mutants :
 * `add`, `PUT {id}`, `close`, `deposit`, `withdraw`, `lock`, `early-unlock`.
 */
export const savingsEndpoints = {
  savingsVaults: {
    /**
     * POST — authentifié (client / marchand / agent pour lui-même). Ouvre un
     * coffre adossé à l'un de SES portefeuilles principaux (`mainWalletId`),
     * dont il reprend la devise. Idempotence obligatoire. 201.
     */
    add: "/gw/savings/api/v1/SavingsVaults/add",
    /**
     * POST — liste paginée, corps `ListRequest<SavingsVaultFilter>`
     * ({ filter: { status?, ownerKeycloakUserId? }, page, pageSize }).
     * `ownerKeycloakUserId` : filtre back-office / service (ignoré client).
     */
    all: "/gw/savings/api/v1/SavingsVaults/all",
    /** GET — détail (soldes, blocage, objectif, progression). 404 hors périmètre (sauf BO). */
    detail: (vaultId: string) => `/gw/savings/api/v1/SavingsVaults/${vaultId}`,
    /**
     * PUT — titulaire uniquement, coffre OUVERT. Renomme / modifie l'objectif
     * (null = sans objectif). Idempotence obligatoire. 422 coffre clos.
     */
    update: (vaultId: string) => `/gw/savings/api/v1/SavingsVaults/${vaultId}`,
    /**
     * POST — titulaire uniquement. Ferme un coffre vide (soldes nuls) ;
     * rejouer sans effet si déjà clos. Idempotence obligatoire. 422 si non vide.
     */
    close: (vaultId: string) =>
      `/gw/savings/api/v1/SavingsVaults/${vaultId}/close`,
    /**
     * POST — titulaire uniquement. Virement principal → coffre, autorisé même
     * pendant un blocage. Idempotence obligatoire. Produit SAVINGS_DEPOSIT,
     * gratuit par défaut.
     */
    deposit: (vaultId: string) =>
      `/gw/savings/api/v1/SavingsVaults/${vaultId}/deposit`,
    /**
     * POST — titulaire uniquement. Coffre → principal. 422 pendant un
     * blocage. Idempotence obligatoire. Produit SAVINGS_WITHDRAWAL, gratuit
     * par défaut.
     */
    withdraw: (vaultId: string) =>
      `/gw/savings/api/v1/SavingsVaults/${vaultId}/withdraw`,
    /**
     * POST — titulaire uniquement. Bloque jusqu'à `lockedUntil` (UTC) ou
     * prolonge un blocage en cours (jamais raccourcir). Fenêtre 1–1 825
     * jours. Idempotence obligatoire.
     */
    lock: (vaultId: string) => `/gw/savings/api/v1/SavingsVaults/${vaultId}/lock`,
    /**
     * POST — flashpay-admin / flashpay-superadmin. Déblocage anticipé en
     * double validation : exige une demande `SAVINGS_EARLY_UNLOCK` APPROVED
     * (resourceType SAVINGS_VAULT, resourceId = coffre) vérifiée en direct
     * auprès d'admin-service. Idempotence obligatoire.
     */
    earlyUnlock: (vaultId: string) =>
      `/gw/savings/api/v1/SavingsVaults/${vaultId}/early-unlock`,
    /** GET — historique paginé des blocages (`?page=&pageSize=`), réponse `Paged`. */
    lockEvents: (vaultId: string) =>
      `/gw/savings/api/v1/SavingsVaults/${vaultId}/lock-events`,
    /** GET — historique paginé des versements / retraits (`?page=&pageSize=`), réponse `Paged`. */
    movements: (vaultId: string) =>
      `/gw/savings/api/v1/SavingsVaults/${vaultId}/movements`,
    /** GET — agrégat des coffres ouverts de l'appelant, par devise. */
    summary: "/gw/savings/api/v1/SavingsVaults/summary",
  },
} as const;
